import { cls } from "app/utils";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import arrow_right from "app/images/arrow-right.svg";
import SingleBooking from "../SingleBooking";
import { Loading } from "app/components/Wrappers";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import { DateTime } from "luxon";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "typedefs";
import UserModel from "app/models/UserModel";
import { hasAcademyPlan } from "app/utils/subscription";
import { hasFeature } from "app/utils/hasFeature";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import FacilityOptionsModal from "./Modals/FacilityOptionsModal";
import { useHistory } from "react-router-dom";
import { useBookingParticipants } from "app/routes/Sessions/useBookingParticipants";
import ViewingChip, { ViewingOption } from "app/routes/Sessions/ViewingChip";
import ParticipantPickerModal from "app/routes/Sessions/ParticipantPickerModal";
import { setBookSession } from "app/actions/scheduling/bookSession";

type ActiveModal = "purchaseOptions" | null;

const formatBookingDate = (
  start: string,
  end: string,
  timeZone: string,
  locale: string,
) => {
  const startDt = DateTime.fromISO(start).setZone(timeZone).setLocale(locale);
  const endDt = DateTime.fromISO(end).setZone(timeZone).setLocale(locale);
  const dateStr = startDt.toLocaleString({
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  const startTime = startDt.toLocaleString(DateTime.TIME_SIMPLE);
  const endTime = endDt.toLocaleString(DateTime.TIME_SIMPLE);
  const tz = startDt.toFormat("ZZZZ");
  return `${dateStr} <b>${startTime} - ${endTime} ${tz}</b>`;
};

const getEventTitle = (event: any, sessionLabel: string) => {
  if (event?.type === "program") {
    return event.title || "";
  }

  if (event?.coachProfile?.firstName) {
    return `${sessionLabel} ${event.coachProfile.firstName} ${event.coachProfile.lastName}`;
  }

  if (event?.displayName) {
    return `${sessionLabel} ${event.displayName}`;
  }

  return sessionLabel;
};

interface MyBookingsProps {
  connectedCoaches?: UserModel[];
}

const MyBookings = ({ connectedCoaches = [] }: MyBookingsProps) => {
  const { t, i18n } = useTranslation();
  const self = useSelector((state: RootState) => state.user.self);
  const history = useHistory();
  const [nextBooking, setNextBooking] = useState(null);
  const [upcomingSession, setUpcomingSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);

  // CN-1323 (CFTB-3278): mirror the Bookings page — a head can scope the widget
  // to themselves, one dependent, or the whole family.
  const { familyMembers, candidates, refetch } = useBookingParticipants();
  const [viewing, setViewing] = useState<string>("self");
  // CN-1323 (CFTB-3282): like the Bookings page, ask which family member the
  // booking is for before entering the availability flow.
  const dispatch = useDispatch();
  const [showParticipantPicker, setShowParticipantPicker] = useState(false);

  const closeModal = () => setActiveModal(null);

  const isSchedulingAthlete =
    (self?.kind === "athlete" || self?.kind === "other") &&
    hasFeature(self?.features, "scheduling");

  const hasCoachWithAcademy = useMemo(
    () => connectedCoaches.some((coach) => hasAcademyPlan(coach)),
    [connectedCoaches],
  );

  useEffect(() => {
    const controller = new AbortController();

    const familyParams =
      viewing === "self"
        ? {}
        : viewing === "all"
          ? { include_family: true }
          : { participant_athlete_id: viewing };

    const fetchEvents = async () => {
      setLoading(true);
      try {
        const data = await schedulingFetchGet(
          "events/next",
          { limit: 2, ...familyParams },
          {},
          controller.signal,
        );
        if (controller.signal.aborted) {
          return;
        }
        const list = Array.isArray(data) ? data : [];
        setNextBooking(list[0] ?? null);
        setUpcomingSession(list[1] ?? null);
      } catch {
        // Expected for athletes with no scheduling profile — the bookings
        // widget just stays empty.
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchEvents();

    return () => controller.abort();
  }, [viewing]);

  if (!isSchedulingAthlete || !hasCoachWithAcademy) {
    return null;
  }

  const renderBookings = () => {
    if (loading) {
      return <Loading isLoading loadType="spinner" />;
    }

    if (!nextBooking) {
      return <p className={styles.empty_state}>{t("No upcoming bookings")}</p>;
    }

    return (
      <>
        <SingleBooking
          badgeType="next"
          title={getEventTitle(nextBooking, t("Session with"))}
          bookingDate={
            nextBooking.start
              ? formatBookingDate(
                  nextBooking.start,
                  nextBooking.end,
                  nextBooking.timeZone,
                  i18n.language,
                )
              : ""
          }
        />
        {upcomingSession && (
          <SingleBooking
            badgeType="upcoming"
            title={getEventTitle(upcomingSession, t("Session with"))}
            bookingDate={
              upcomingSession.start
                ? formatBookingDate(
                    upcomingSession.start,
                    upcomingSession.end,
                    upcomingSession.timeZone,
                    i18n.language,
                  )
                : ""
            }
          />
        )}
      </>
    );
  };

  const onBookaSessionClick = () => {
    // With managed family members, pick the participant first; otherwise this is
    // a plain self-booking and we go straight into the availability flow.
    if (familyMembers.length > 0) {
      setShowParticipantPicker(true);
    } else {
      history.push("/sessions/session_availability");
    }
  };

  const handleConfirmParticipant = (participantAthleteId: string | null) => {
    dispatch(setBookSession({ participantAthleteId }));
    setShowParticipantPicker(false);
    history.push("/sessions/session_availability");
  };

  const viewingOptions: ViewingOption[] = [
    {
      value: "self",
      label: t("My Bookings"),
      avatar: (self?.avatar as string) ?? null,
    },
    ...familyMembers.map((m) => ({
      value: m.userId,
      label: `${m.firstName} ${m.lastName}`.trim(),
      avatar: m.avatar,
    })),
    { value: "all", label: t("All Family"), avatar: null },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.header_wrapper}>
          {familyMembers.length > 0 ? (
            <ViewingChip
              options={viewingOptions}
              value={viewing}
              onChange={setViewing}
            />
          ) : (
            <h5>{t("My Bookings")}</h5>
          )}
        </div>
        <img
          src={arrow_right}
          alt="forward arrow"
          className={styles.forward_icon}
          onClick={() => history.push("/sessions")}
          style={{ cursor: "pointer" }}
        />
      </div>
      <div className={styles.booking_container}>{renderBookings()}</div>
      <div className={styles.buttons_wrapper}>
        <button
          onClick={onBookaSessionClick}
          className={cls(styles.button, styles.filled)}
        >
          {t("Book a session")}
        </button>
        <button
          className={cls(styles.button, styles.outline)}
          onClick={() => setActiveModal("purchaseOptions")}
        >
          {t("Purchase options")}
        </button>
      </div>

      <ParticipantPickerModal
        isOpen={showParticipantPicker}
        candidates={candidates}
        onClose={() => setShowParticipantPicker(false)}
        onConfirm={handleConfirmParticipant}
        onMemberAdded={refetch}
        defaultParticipantId={viewing === "self" || viewing === "all" ? null : viewing}
      />

      <ModalLayout
        isOpen={activeModal !== null}
        onClose={closeModal}
        classNames={styles.modal_container}
      >
        {activeModal === "purchaseOptions" && (
          <FacilityOptionsModal onClose={closeModal} />
        )}
      </ModalLayout>
    </div>
  );
};

export default MyBookings;
